import { z } from 'zod';
import {
  AdminCompanyEmailDomainReasonSchema,
  BillingEntityOwnedOrganizationSchema,
} from './billing-entity.types.js';
import { coreIdSchema } from './ids.js';

/**
 * Verified email-domain claims (enterprise identity work package E2).
 *
 * A claim is one billing entity's assertion of control over an email
 * domain, proved with a DNS TXT challenge and consumed later by SSO
 * discovery and auto-join routing. Domains are a company-level
 * association: one billing entity may own several organizations
 * (engineering and ops under one contract), so the claim belongs to the
 * billing entity and each consumption choice names how the company's
 * organizations use the proof. The claim is the proof; consumption is a
 * separate, per-feature opt-in (`ssoDiscoveryEnabled`,
 * `autoJoinOrganizationId`) so losing or disabling one use never silently
 * changes another.
 *
 * See docs/internal/architecture/verified-domains.md.
 */

/**
 * `pending` — challenge issued, proof not yet observed in DNS.
 * `verified` — proof observed; re-checked periodically.
 * `lost` — proof was verified once but a re-check definitively no longer
 * finds it. A lost claim stops SSO discovery and auto-join routing but
 * never removes memberships that were created while it was verified.
 */
export const BillingEntityDomainClaimStatusSchema = z.enum(['pending', 'verified', 'lost']);
export type BillingEntityDomainClaimStatus = z.infer<typeof BillingEntityDomainClaimStatusSchema>;

/**
 * How the proof was established.
 *
 * `dns-txt` — the normal path: a TXT record at the Reflex challenge name,
 * observed through public DNS.
 * `platform-approved` — an audited platform-admin exception for dedicated
 * VPC deployments whose DNS is split-horizon and cannot publish the
 * challenge publicly. Never selectable by org admins.
 */
export const DomainVerificationMethodSchema = z.enum(['dns-txt', 'platform-approved']);
export type DomainVerificationMethod = z.infer<typeof DomainVerificationMethodSchema>;

/**
 * DNS label the TXT challenge lives under: the org admin publishes
 * `_reflex-domain-verification.<domain> TXT "<token>"`.
 */
export const DOMAIN_CLAIM_RECORD_LABEL = '_reflex-domain-verification';

/** The full record name the challenge TXT must be published at. */
export function domainClaimRecordName(domain: string): string {
  return `${DOMAIN_CLAIM_RECORD_LABEL}.${domain}`;
}

/**
 * Consumer mailbox providers. A customer cannot prove control of these —
 * publishing our TXT record under `gmail.com` is impossible for a tenant,
 * and a claim on one would route every consumer sign-in on the deployment.
 * Rejected at the API boundary, kept here so the E3 form can reject them
 * client-side with the same list.
 */
export const KNOWN_CONSUMER_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'fastmail.com',
  'hey.com',
  'qq.com',
  '163.com',
  '126.com',
  'yandex.com',
  'yandex.ru',
];

/**
 * Multi-label public suffixes a registrant cannot own outright. Single-label
 * suffixes (`com`, `io`, …) are already unreachable because
 * `EmailDomainSchema` requires at least two labels; this list covers the
 * common registry-controlled second levels. Deliberately curated rather than
 * the full Public Suffix List — a false negative here still requires the
 * claimant to control the registry's own DNS to pass verification.
 */
export const KNOWN_PUBLIC_SUFFIXES: readonly string[] = [
  'co.uk',
  'org.uk',
  'ac.uk',
  'gov.uk',
  'me.uk',
  'com.au',
  'net.au',
  'org.au',
  'co.nz',
  'co.jp',
  'or.jp',
  'ne.jp',
  'co.kr',
  'co.in',
  'com.br',
  'com.cn',
  'com.mx',
  'com.sg',
  'com.tr',
  'co.za',
];

/**
 * Whether `domain` (already normalized and lower-cased) is a domain no
 * customer may claim: a consumer mailbox provider or a public suffix
 * itself. Subdomains of public suffixes (`example.co.uk`) remain claimable.
 */
export function isUnclaimableDomain(domain: string): boolean {
  return KNOWN_CONSUMER_EMAIL_DOMAINS.includes(domain) || KNOWN_PUBLIC_SUFFIXES.includes(domain);
}

export const BillingEntityDomainClaimSchema = z
  .object({
    id: z.string(),
    /** The billing entity (commercial owner) that holds the proof. */
    billingEntityId: z.string(),
    /** Normalized (lower-case, punycode) email domain being claimed. */
    domain: z.string(),
    status: BillingEntityDomainClaimStatusSchema,
    method: DomainVerificationMethodSchema,
    /**
     * Carried in from the pre-verification domain table and never vetted.
     * Routes as before, but is not proof for attaching an existing account
     * until the company verifies it by DNS or a platform admin approves it.
     */
    grandfathered: z.boolean(),
    /** Full DNS record name the TXT challenge must be published at. */
    recordName: z.string(),
    /**
     * The challenge value to publish. Shown to org admins — it is proof
     * material to be made public in DNS, never authentication material.
     */
    challengeToken: z.string(),
    /**
     * Whether verified proof may route SSO discovery for this domain to
     * the billing entity's organizations.
     */
    ssoDiscoveryEnabled: z.boolean(),
    /**
     * The organization unprovisioned sign-ups for this domain auto-join,
     * or null when auto-join is off. Must be an organization the billing
     * entity owns; at most one active destination exists per domain
     * across all billing entities (enforced transactionally).
     */
    autoJoinOrganizationId: z.string().nullable(),
    verifiedAt: z.number().nullable(),
    lastCheckedAt: z.number().nullable(),
    nextCheckAt: z.number().nullable(),
    /** Consecutive definitive re-check misses; resets on success. */
    failedChecks: z.number(),
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .meta({ id: 'BillingEntityDomainClaim' });
export type BillingEntityDomainClaim = z.infer<typeof BillingEntityDomainClaimSchema>;

export const CreateBillingEntityDomainClaimRequestSchema = z.object({
  /**
   * Deliberately looser than `EmailDomainSchema`: internationalized input
   * (`bücher.example`) must reach the server's punycode normalizer, whose
   * ASCII-only validation runs *after* conversion. Only shape limits here.
   */
  domain: z.string().trim().min(1, 'Domain is required').max(253, 'Domain is too long'),
});
export type CreateBillingEntityDomainClaimRequest = z.infer<
  typeof CreateBillingEntityDomainClaimRequestSchema
>;

/**
 * A platform admin claiming a domain on a company's behalf. The claim still
 * lands `pending` and must be proved by DNS (or approved separately); the
 * reason records why support, not the company, started it.
 */
export const AdminCreateBillingEntityDomainClaimRequestSchema =
  CreateBillingEntityDomainClaimRequestSchema.extend({
    reason: AdminCompanyEmailDomainReasonSchema,
  }).strict();
export type AdminCreateBillingEntityDomainClaimRequest = z.infer<
  typeof AdminCreateBillingEntityDomainClaimRequestSchema
>;

/** Support removing one company's claim from its admin page; audited. */
export const AdminRemoveBillingEntityDomainClaimRequestSchema = z
  .object({ reason: AdminCompanyEmailDomainReasonSchema })
  .strict();
export type AdminRemoveBillingEntityDomainClaimRequest = z.infer<
  typeof AdminRemoveBillingEntityDomainClaimRequestSchema
>;

const DomainClaimPolicyFieldsSchema = z.object({
  ssoDiscoveryEnabled: z.boolean().optional(),
  /**
   * Set the domain's auto-join destination to one of the billing
   * entity's organizations, or clear it with null. Omitted means
   * unchanged.
   */
  autoJoinOrganizationId: coreIdSchema('organization').nullable().optional(),
});

const hasPolicyField = (v: z.infer<typeof DomainClaimPolicyFieldsSchema>) =>
  v.ssoDiscoveryEnabled !== undefined || v.autoJoinOrganizationId !== undefined;
const POLICY_FIELD_REQUIRED = { message: 'Provide at least one policy field to update.' };

export const UpdateBillingEntityDomainClaimPolicyRequestSchema =
  DomainClaimPolicyFieldsSchema.refine(hasPolicyField, POLICY_FIELD_REQUIRED);
export type UpdateBillingEntityDomainClaimPolicyRequest = z.infer<
  typeof UpdateBillingEntityDomainClaimPolicyRequestSchema
>;

/**
 * The platform-admin override of a claim's policy. Support is changing
 * another company's routing across the tenancy wall, so it carries a
 * written reason for the audit row, like every other admin mutation here.
 */
export const AdminUpdateBillingEntityDomainClaimPolicyRequestSchema =
  DomainClaimPolicyFieldsSchema.extend({
    reason: AdminCompanyEmailDomainReasonSchema,
  }).refine(hasPolicyField, POLICY_FIELD_REQUIRED);
export type AdminUpdateBillingEntityDomainClaimPolicyRequest = z.infer<
  typeof AdminUpdateBillingEntityDomainClaimPolicyRequestSchema
>;

export const ApproveBillingEntityDomainClaimRequestSchema = z.object({
  /** Why public DNS proof is impossible for this deployment; audited. */
  reason: z.string().trim().min(10).max(2000),
});
export type ApproveBillingEntityDomainClaimRequest = z.infer<
  typeof ApproveBillingEntityDomainClaimRequestSchema
>;

/** Outcome of an on-demand verification attempt. */
export const BillingEntityDomainClaimVerifyResponseSchema = z.object({
  claim: BillingEntityDomainClaimSchema,
  /**
   * `verified` — proof observed. `not-found` — DNS answered and the record
   * is definitively absent. `error` — resolver failure; nothing was
   * concluded and the claim is unchanged.
   */
  outcome: z.enum(['verified', 'not-found', 'error']),
});
export type BillingEntityDomainClaimVerifyResponse = z.infer<
  typeof BillingEntityDomainClaimVerifyResponseSchema
>;

/**
 * Enterprise identity audit trail. Every entitlement-relevant mutation —
 * domain lifecycle now; IdP, require-SSO, and break-glass events as their
 * packages land — records who did what to which subject.
 */
export const EnterpriseIdentityAuditActionSchema = z.enum([
  'domain-claim.created',
  'domain-claim.verified',
  'domain-claim.verification-lost',
  'domain-claim.policy-changed',
  'domain-claim.removed',
  'domain-claim.platform-approved',
  'domain-claim.reassigned',
]);
export type EnterpriseIdentityAuditAction = z.infer<typeof EnterpriseIdentityAuditActionSchema>;

export const EnterpriseIdentityAuditEventSchema = z
  .object({
    id: z.string(),
    /** The billing entity whose identity surface the action touched. */
    billingEntityId: z.string(),
    action: EnterpriseIdentityAuditActionSchema,
    /** Id of the row the action applied to (today always a domain claim). */
    subjectId: z.string(),
    /**
     * NULL for system actors — the re-check sweep records
     * verification-lost with no acting user.
     */
    actorUserId: z.string().nullable(),
    /** Action-specific detail (domain, method, changed fields, reason). */
    detail: z.record(z.string(), z.unknown()),
    createdAt: z.number(),
  })
  .meta({ id: 'EnterpriseIdentityAuditEvent' });
export type EnterpriseIdentityAuditEvent = z.infer<typeof EnterpriseIdentityAuditEventSchema>;

/**
 * The org-scoped read: every claim held by the organization's billing
 * entity, plus the sibling organizations the entity owns — the candidate
 * destinations for the auto-join policy picker. The surface is company
 * scoped, so an organization with no company yet has nothing to read here.
 */
/**
 * Platform-admin claim lookup. One of the two filters is required: an
 * unfiltered listing would be a cross-tenant dump of every customer's
 * domains, which no support task needs.
 */
export const AdminListDomainClaimsQuerySchema = z
  .object({
    domain: z.string().optional(),
    billingEntityId: coreIdSchema('billingEntity').optional(),
  })
  .refine((value) => value.domain !== undefined || value.billingEntityId !== undefined, {
    message: 'Provide a domain or billingEntityId',
  });
export type AdminListDomainClaimsQuery = z.infer<typeof AdminListDomainClaimsQuerySchema>;

export const ListBillingEntityDomainClaimsResponseSchema = z.object({
  claims: z.array(BillingEntityDomainClaimSchema),
  /** Organizations owned by the governing billing entity (self included). */
  organizations: z.array(BillingEntityOwnedOrganizationSchema),
});
export type ListBillingEntityDomainClaimsResponse = z.infer<
  typeof ListBillingEntityDomainClaimsResponseSchema
>;

export const ListEnterpriseIdentityAuditEventsResponseSchema = z.object({
  events: z.array(EnterpriseIdentityAuditEventSchema),
});
export type ListEnterpriseIdentityAuditEventsResponse = z.infer<
  typeof ListEnterpriseIdentityAuditEventsResponseSchema
>;
